import PropTypes from "prop-types";
import { createContext, useEffect, forwardRef, Fragment } from "react";
import { useLocation, Link as RouterLink, Outlet } from "react-router-dom";
import { useImmer } from "use-immer";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Slide from "@mui/material/Slide";
// import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";

import SvgIconStyle from "src/components/SvgIconStyle";
import LazySize from "src/components/LazySize";
import MaxLineText from "src/components/MaxLineText";
import { AHidden } from "src/components/@material-extend";
import RelatedMovieGrid from "src/pages/components/RelatedMovieGrid";
import SeasonListInDetailModal from "src/pages/components/SeasonListInDetailModal";
import { useDispatch, useSelector } from "src/redux";
import { fetchOptions } from "src/redux/slices/filter";
import { PATH_DASHBOARD } from "src/routes/paths";
import MyListAddButton from "src/components/MyListAddButton";
import MyListRemoveButton from "src/components/MyListRemoveButton";
import { addItemToMyList, deleteItemFromMyList } from "src/redux/slices/myList";
import { fetchWithURL } from "src/apis/main";
import { formatTimeToReadable } from "src/utils/formatTime";

const DialogStyle = styled(Dialog)(({ theme }) => ({
  [theme.breakpoints.down("sm")]: {
    "& > .MuiDialog-container > .MuiPaper-root": {
      // width: "calc(100% - 15px)",
      // maxWidth: "calc(100% - 15px)",
      width: "100%",
      maxWidth: "100%",
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      marginTop: 64,
    },
  },
}));

const DetailModalContext = createContext();

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

DetailModalProvider.propTypes = {
  children: PropTypes.node,
};

function DetailModalProvider({ children }) {
  const [item, setItem] = useImmer(null);
  const genres = useSelector((state) => state.filter.genre);
  const languages = useSelector((state) => state.filter.lang);
  const { myList } = useSelector((state) => state.myList);
  const location = useLocation();
  const dispatch = useDispatch();

  const handleClose = () => {
    setItem(null);
  };

  useEffect(() => {
    setItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    dispatch(fetchOptions({ by: "genre" }));
    dispatch(fetchOptions({ by: "lang" }));
  }, [dispatch]);

  useEffect(() => {
    async function getFeedData() {
      if (item && !item.feedData) {
        const response = await fetchWithURL(item.feedUrl);
        if (response.data) {
          setItem((draft) => {
            draft.feedData = response.data;
          });
        }
      }
    }
    getFeedData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]);

  console.log("Detail: ", item);

  return (
    <DetailModalContext.Provider
      value={{
        setDetailModal: (v) => setItem(v),
      }}
    >
      <Outlet />
      {children}
      {item && (
        <DialogStyle
          fullWidth
          scroll="body"
          onClose={() => {
            setItem(null);
          }}
          maxWidth="md"
          open={item !== null}
          TransitionComponent={Transition}
        >
          <DialogContent
            sx={{ p: 0, pb: { xs: 1, md: 2 }, bgcolor: "background.default" }}
          >
            <Fragment>
              <Box sx={{ position: "relative" }}>
                <Box
                  sx={{
                    width: "100%",
                    position: "relative",
                    paddingTop: "calc(9 / 16 * 100%)",
                  }}
                >
                  <Box
                    sx={{
                      top: 0,
                      width: 1,
                      height: 1,
                      zIndex: 9,
                      position: "absolute",
                      backgroundImage: (theme) =>
                        // `radial-gradient(farthest-side at 50% 50%, transparent, transparent, ${theme.palette.background.default})`,
                        // `radial-gradient(transparent 5%, transparent 5%, ${theme.palette.background.default} 80%)`,
                        `linear-gradient(to bottom left, transparent , ${theme.palette.background.default})`,
                    }}
                  />
                  <LazySize
                    alt={item.title}
                    src={item.poster}
                    hidePlaceholder={true}
                  />
                  <IconButton
                    onClick={handleClose}
                    sx={{
                      top: 15,
                      right: 15,
                      zIndex: 11,
                      position: "absolute",
                      bgcolor: "background.default",
                      width: { xs: 22, sm: 40 },
                      height: { xs: 22, sm: 40 },
                      "&:hover": {
                        bgcolor: "secondary.darker",
                      },
                    }}
                  >
                    <CloseIcon
                      sx={{ color: "white", fontSize: { xs: 14, sm: 22 } }}
                    />
                  </IconButton>
                  <Stack
                    spacing={3}
                    sx={{
                      top: 0,
                      width: 1,
                      height: 1,
                      zIndex: 10,
                      position: "absolute",
                      px: { xs: 2, sm: 3, md: 5 },
                      pt: { xs: 2, sm: 3, md: 5 },
                      pb: 2,
                    }}
                  >
                    <Box>
                      <MaxLineText
                        variant="h3"
                        maxLine={1}
                        maxWidth={{
                          xs: 220,
                          md: 500,
                        }}
                        text={item.title}
                      />
                      {item.feedData && (
                        <Typography variant="body2" gutterBottom>
                          {formatTimeToReadable(item.feedData.duration * 60)}
                        </Typography>
                      )}
                      {item.feedData && (
                        <Typography variant="body2" gutterBottom>
                          {item.feedData.releaseYear}
                        </Typography>
                      )}
                    </Box>
                    <AHidden width="smDown">
                      <Box flexGrow={1} />
                    </AHidden>
                    <Stack spacing={{ xs: 2, sm: 3 }}>
                      <Stack
                        direction="row"
                        spacing={{ xs: 2, md: 4 }}
                        alignItems="center"
                      >
                        <Button
                          color="inherit"
                          variant="contained"
                          component={RouterLink}
                          to={`${PATH_DASHBOARD.watch}/${item.contentId}`}
                          state={{ prevPagePath: location.pathname }}
                          startIcon={
                            <SvgIconStyle src="/static/vectors/play.svg" />
                          }
                          sx={{
                            fontSize: { xs: 14, md: 18 },
                            borderRadius: "8px",
                            color: "background.default",
                          }}
                        >
                          Play
                        </Button>
                        {!myList.includes(item.id) ? (
                          <MyListAddButton
                            onClick={() => {
                              dispatch(
                                addItemToMyList({
                                  contentId: item.id,
                                  type: item.type,
                                })
                              );
                            }}
                            sx={{
                              width: { xs: 22, sm: 40 },
                              height: { xs: 22, sm: 40 },
                            }}
                          />
                        ) : (
                          <MyListRemoveButton
                            onClick={() => {
                              dispatch(
                                deleteItemFromMyList({
                                  id: item.id,
                                  type: item.type,
                                })
                              );
                            }}
                            sx={{
                              width: { xs: 22, sm: 40 },
                              height: { xs: 22, sm: 40 },
                            }}
                          />
                        )}
                      </Stack>
                      <Box display="flex" sx={{ width: 1, my: 2 }}>
                        <Box>
                          {item.feedData && (
                            <Typography variant="body1" fontWeight={700}>
                              {`HD | ${
                                item.feedData.releaseYear
                              } | ${formatTimeToReadable(
                                item.feedData.duration * 60
                              )} | 12+`}
                            </Typography>
                          )}
                          <Typography variant="body1" fontWeight={700}>
                            {`Genre : ${item.categoryId
                              .split(",")
                              .map(
                                (i) =>
                                  genres.find((g) => g.id.toString() === i).name
                              )
                              .join(", ")}`}
                          </Typography>
                        </Box>
                        <Box flexGrow={1} />
                        <Box>
                          <Typography variant="body1" fontWeight={700}>
                            {`Available in : ${item.languageId
                              .split(",")
                              .map(
                                (i) =>
                                  languages.find((g) => g.id.toString() === i)
                                    .name || ""
                              )
                              .join(", ")}`}
                          </Typography>
                          {item.feedData && (
                            <Typography variant="body1" fontWeight={700}>
                              {`Starring : ${item.feedData.actors
                                .map((i) => i.name)
                                .join(", ")}`}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                      {item.feedData && (
                        <MaxLineText
                          maxLine={3}
                          variant="body1"
                          text={item.feedData.description}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>
              </Box>

              {item.type === "movie" && (
                <RelatedMovieGrid url={item.relatedURL} />
              )}

              {item.type === "series" && (
                <SeasonListInDetailModal url={item.feedUrl} />
              )}
            </Fragment>
          </DialogContent>
        </DialogStyle>
      )}
    </DetailModalContext.Provider>
  );
}

export { DetailModalContext, DetailModalProvider };
